/**
 * A headless copy of the document editor (git sync Stage 2, card S2.1): the
 * same node classes as LexicalRichEditor, no DOM view. Every comparison
 * runs both sides through `normalize` here, so "unchanged" means "the editor
 * ends up with the same document" (list numbering, nested-list wrappers and
 * indents included).
 */
import { createEditor, $getRoot, $parseSerializedNode } from 'lexical';
import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { ListNode, ListItemNode } from '@lexical/list';
import { LinkNode } from '@lexical/link';
import { CodeNode, CodeHighlightNode } from '@lexical/code';
import { TableNode, TableCellNode, TableRowNode } from '@lexical/table';
import { PageLinkNode } from '../../components/ui/lexicalNodes/PageLinkNode';
import { DocImageNode } from '../../components/ui/lexicalNodes/DocImageNode';
import { DocVideoEmbedNode } from '../../components/ui/lexicalNodes/DocVideoEmbedNode';
import { CalloutNode } from '../../components/ui/lexicalNodes/CalloutNode';
import { codeText } from './jsonNodes';

/** Keep in step with EDITOR_NODES in components/ui/LexicalRichEditor.js (a test checks it). */
export const DOC_NODES = [
  HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, CodeNode, CodeHighlightNode,
  TableNode, TableCellNode, TableRowNode, PageLinkNode, DocImageNode, DocVideoEmbedNode, CalloutNode,
];

export function createDocEditor() {
  return createEditor({
    namespace: 'apiboo-doc-markdown',
    nodes: DOC_NODES,
    onError: (e) => {
      throw e;
    },
  });
}

/** Run `build` (which appends to the root) in a fresh headless editor; returns the root's children as JSON. */
export function buildChildren(build) {
  const editor = createDocEditor();
  editor.update(
    () => {
      const root = $getRoot();
      root.clear();
      build(root);
    },
    { discrete: true }
  );
  return editor.getEditorState().toJSON().root.children;
}

/**
 * Block JSON → the JSON the editor itself ends up with: inserted in an update,
 * so Lexical's transforms run (list numbering, nested-list wrappers).
 * Used for BOTH sides of every comparison.
 */
export function normalize(children) {
  return buildChildren((root) => {
    for (const node of children || []) root.append($parseSerializedNode(node));
  });
}

/**
 * The comparable form of one block. Code blocks compare by language + text,
 * not by how the highlighter split the tokens (the editor re-tokenizes on load).
 */
export function comparable(node) {
  if (!node || typeof node !== 'object') return node;
  if (node.type === 'code') {
    return { type: 'code', language: node.language || '', text: codeText(node), indent: node.indent || 0, format: node.format || '' };
  }
  if (node.type === 'paragraph' || node.type === 'heading' || node.type === 'quote' || node.type === 'callout' || node.type === 'listitem' || node.type === 'tablecell') {
    // `direction` follows the text, `textFormat`/`textStyle` remember the caret's last format —
    // neither is content: the editor recomputes direction from the text on every update, and the
    // caret format only seeds the next typed character.
    const { direction, textFormat, textStyle, ...rest } = node;
    return { ...rest, children: (node.children || []).map(comparable) };
  }
  if (Array.isArray(node.children)) {
    const { direction, ...rest } = node;
    return { ...rest, children: node.children.map(comparable) };
  }
  return node;
}
